/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  hasMany,
  belongsTo,
  attr,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Period from '@linc-technologies/linced-common/models/timetable/period';
import moment from 'moment';
import type Record from 'linced-app/models/attendance/record';
import type Date from 'linced-app/models/date';
import type Group from 'linced-app/models/group';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Proto: attendance.Register.
export default class Register extends Model {
  declare [Type]: 'attendance/register';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  @computed('createTime')
  get createTimeFormattedLong() {
    return this.createTime
      ? moment.unix(this.createTime).format('ll [at] LT')
      : '';
  }

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the attendance register is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the school campus the attendance register is assigned to.
  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  // dateId is the date the attendance is assigned to.
  @belongsTo('date', { async: true, inverse: null })
  declare dateId: AsyncBelongsTo<Date>;

  @alias('dateId')
  declare dateObj: AsyncBelongsTo<Date>;

  // date contains the date in ISO 8601 format (YYYY-MM-DD)
  @attr('string')
  declare date: string | undefined;

  // periodId is the schooling class period the attendance is assigned to.
  @belongsTo('timetable/period', { async: true, inverse: null })
  declare periodId: AsyncBelongsTo<Period>;

  @alias('periodId')
  declare period: AsyncBelongsTo<Period>;

  // groupId is the group the register is assigned to.
  @belongsTo('group', { async: true, inverse: null })
  declare groupId: AsyncBelongsTo<Group>;

  @alias('groupId')
  declare group: AsyncBelongsTo<Group>;

  @alias('group.id')
  declare group_id: string | null | undefined;

  // authorId is the staff member who marked the attendance register.
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  // recordIds is a list of attendance record resources that this register is
  // linked to.
  @hasMany('attendance/record', { async: true, inverse: null })
  declare recordIds: AsyncHasMany<Record>;

  @alias('recordIds')
  declare records: AsyncHasMany<Record>;
}
